/**
 * Update Limits Modal Component - Unified Admin Module
 * 
 * Reusable modal for updating user limits on existing monthly plans.
 * Used in user detail views for consistency.
 */

import React, { useState, useEffect } from 'react';
import { Input, FormAlert } from '../../components';
import { Button, Icon, Text } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import { useAdminData } from '../hooks/useAdminData';
import { getPlanInfo } from '../utils';
import type { PlanType, UserIdentifier } from '../types';

interface UpdateLimitsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  userIdentifier: UserIdentifier;
  currentPlan: PlanType;
  currentWeeklyAllowance?: number;
  currentMonthlyAllowance?: number;
  userDisplayName?: string;
}

/**
 * Modal for updating limits on existing monthly plans
 */
export const UpdateLimitsModal: React.FC<UpdateLimitsModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  userIdentifier,
  currentPlan,
  currentWeeklyAllowance,
  currentMonthlyAllowance,
  userDisplayName
}) => {
  const { operations, billingConfig } = useAdminData();
  const [isClosing, setIsClosing] = useState(false);

  // Form state
  const [limitsForm, setLimitsForm] = useState({
    monthlyLimit: ''
  });

  // Load billing configuration when modal opens
  useEffect(() => {
    if (isOpen) {
      billingConfig.loadConfig();
      setLimitsForm({ monthlyLimit: '' });
    }
  }, [isOpen]);

  const handleUpdateLimits = async () => {
    if (!userIdentifier) return;

    let monthlyLimit: number | undefined;

    if (limitsForm.monthlyLimit) {
      monthlyLimit = parseFloat(limitsForm.monthlyLimit);
      if (isNaN(monthlyLimit) || monthlyLimit < 0) {
        alert('Please enter a valid monthly limit');
        return;
      }
    }

    if (monthlyLimit === undefined) {
      alert('Please enter a monthly limit to update');
      return;
    }

    const success = await operations.updateLimits(userIdentifier, undefined, monthlyLimit);

    if (success) {
      setLimitsForm({ monthlyLimit: '' });
      onSuccess();
      handleClose();
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      setLimitsForm({ monthlyLimit: '' });
      operations.clearError();
      onClose();
    }, 300);
  };

  const title = `Update Limits${userDisplayName ? ` for ${userDisplayName}` : ''}`;

  if (!isOpen && !isClosing) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-alpha-black-70 z-[200] ${isClosing ? 'animate-fade-out' : 'animate-fade-in'}`}
        onClick={handleClose}
      />
      {/* Panel */}
      <div
        className={`fixed right-0 top-0 h-screen z-[200] bg-bg-primary w-[420px] flex flex-col border-l border-border-secondary-alt ${isClosing ? 'animate-slide-out-right' : 'animate-slide-in-right'}`}
        style={{
          boxShadow: '0 20px 24px -4px rgba(10, 13, 18, 0.08), 0 8px 8px -4px rgba(10, 13, 18, 0.03), 0 3px 3px -1.5px rgba(10, 13, 18, 0.04)',
        }}
      >
        {/* Header */}
        <div className="flex p-bg-v2-3xl items-center gap-bg-v2-md self-stretch justify-between">
          <Text variant="text-xl" weight="bold">{title}</Text>
          <button
            onClick={handleClose}
            className="flex items-center justify-center w-8 h-8 rounded-full text-fg-secondary hover:bg-bg-primary-hover hover:text-text-secondary-hover transition-all duration-200"
          >
            <Icon name={IconName.Cross} size="lg" inheritColor />
          </button>
        </div>

        {/* Divider */}
        <div className="box-border flex flex-col gap-[16px] items-center px-[var(--spacing-3xl,24px)] py-0 w-full">
          <div className="bg-border-secondary h-px w-full" />
        </div>

        {/* Content */}
        <div className="flex flex-col gap-bg-v2-3xl self-stretch px-bg-v2-3xl py-bg-v2-3xl overflow-y-auto flex-1">
          {currentPlan === 'monthly' && (
            <Input
              label={
                billingConfig.config
                  ? `Monthly Limit ($) — Current: $${currentMonthlyAllowance}, Default: $${billingConfig.config.default_monthly_allowance}`
                  : 'Monthly Limit ($)'
              }
              type="number"
              step="0.01"
              min="0"
              max="10000"
              value={limitsForm.monthlyLimit}
              onChange={(e) => setLimitsForm(prev => ({ ...prev, monthlyLimit: e.target.value }))}
              placeholder={currentMonthlyAllowance?.toString() || '50.00'}
            />
          )}

          <div className="bg-bg-brand-primary p-bg-v2-lg rounded-bg-v2-md">
            <p className="text-sm text-text-brand-secondary">
              <strong>Current Plan:</strong> {getPlanInfo(currentPlan).label}
            </p>
            <p className="text-sm text-text-brand-primary mt-bg-v2-xxs">
              Leave fields empty to keep current values. Only enter values you want to change.
            </p>
          </div>

          {operations.error && (
            <FormAlert message={operations.error} variant="error" />
          )}
        </div>

        {/* Footer */}
        <div className="flex p-bg-v2-xl px-bg-v2-3xl justify-end items-center gap-bg-v2-xl self-stretch border-t border-border-secondary bg-bg-primary">
          <Button
            variant="outline"
            label="Cancel"
            onClick={handleClose}
            disabled={operations.loading}
          />
          <Button
            variant="primary"
            label={operations.loading ? 'Updating...' : 'Update Limits'}
            onClick={handleUpdateLimits}
            disabled={operations.loading}
          />
        </div>
      </div>
    </>
  );
};

export default UpdateLimitsModal;
